import { useState, useEffect } from "react";
import { Repeat, CheckCircle, Clock, X, LogIn, ChevronRight, Calendar, Package } from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";
import { useAppContext } from "../../../context";
import { Link } from "react-router-dom";

const CYCLE_LABEL = { weekly: "/ week", biweekly: "/ 2 weeks", monthly: "/ month" };
const STATUS_CHIP = {
  active:    { cls: "bg-green-100 text-green-800",  label: "Active"    },
  paused:    { cls: "bg-yellow-100 text-yellow-800",label: "Paused"    },
  cancelled: { cls: "bg-red-100 text-red-700",      label: "Cancelled" },
};

const PlanCard = ({ plan, onSubscribe, myActivePlanIds, loading }) => {
  const isActive = myActivePlanIds.includes(plan.id);
  return (
    <div className={`bg-white rounded-2xl border overflow-hidden flex flex-col transition hover:shadow-md ${isActive ? "ring-2 ring-orange-400" : ""}`}>
      {isActive && (
        <div className="bg-orange-500 text-white text-xs font-bold text-center py-1.5 tracking-wide">
          CURRENT PLAN
        </div>
      )}
      <div className="p-6 flex flex-col gap-4 flex-1">
        <div>
          <h3 className="text-lg font-extrabold text-gray-900">{plan.name}</h3>
          {plan.description && <p className="text-sm text-gray-500 mt-1">{plan.description}</p>}
        </div>
        <div className="flex items-end gap-1">
          <span className="text-4xl font-extrabold text-orange-500">${Number(plan.price).toFixed(2)}</span>
          <span className="text-gray-400 text-sm mb-1">{CYCLE_LABEL[plan.billing_cycle] || `/${plan.billing_cycle}`}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
          <Package size={15} className="text-orange-400 flex-shrink-0" />
          <span><strong>{plan.meals_per_cycle}</strong> meals per cycle</span>
        </div>
        <div className="mt-auto space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> Free delivery on all meals</div>
          <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> Pause or cancel anytime</div>
          <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /> Priority support</div>
        </div>
      </div>
      <div className="px-6 pb-6">
        <button
          onClick={() => onSubscribe(plan)}
          disabled={loading || isActive}
          className={`w-full py-3 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 ${
            isActive
              ? "bg-gray-100 text-gray-400 cursor-default"
              : "bg-orange-500 hover:bg-orange-600 text-white"
          }`}
        >
          {isActive ? "Subscribed" : loading ? "Processing..." : <>Subscribe <ChevronRight size={16} /></>}
        </button>
      </div>
    </div>
  );
};

const SubscriptionsPage = () => {
  const { customerUser } = useAppContext();
  const [plans, setPlans]       = useState([]);
  const [mySubs, setMySubs]     = useState([]);
  const [tab, setTab]           = useState("plans");
  const [loading, setLoading]   = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [msg, setMsg]           = useState(null); // { type: ok|error, text }

  useEffect(() => {
    axios.get(`${apiUrl}/subscriptions/plans/active?license_id=2`)
      .then((r) => setPlans(r.data?.list || []));
    if (customerUser?.id) loadMySubs();
  }, [customerUser]);

  const loadMySubs = async () => {
    if (!customerUser?.id) return;
    const res = await axios.get(`${apiUrl}/subscriptions/user/${customerUser.id}`);
    setMySubs(res.data?.list || []);
  };

  const handleSubscribe = async (plan) => {
    if (!customerUser) {
      setMsg({ type: "error", text: "Please sign in to subscribe." });
      return;
    }
    setSubLoading(true);
    setMsg(null);
    try {
      const res = await axios.post(`${apiUrl}/subscriptions/subscribe`, {
        user_id: customerUser.id,
        plan_id: plan.id,
        start_date: new Date().toISOString().split("T")[0],
      });
      if (res.data?.status) {
        setMsg({ type: "ok", text: `You're now subscribed to ${plan.name}!` });
        await loadMySubs();
        setTab("my");
      } else {
        setMsg({ type: "error", text: res.data?.error || "Failed to subscribe." });
      }
    } catch {
      setMsg({ type: "error", text: "Something went wrong. Please try again." });
    }
    setSubLoading(false);
  };

  const handleStatusChange = async (id, status) => {
    await axios.put(`${apiUrl}/subscriptions/${id}/status`, { status });
    await loadMySubs();
  };

  const myActivePlanIds = mySubs.filter((s) => s.status === "active").map((s) => s.plan_id);

  if (!customerUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Repeat size={30} className="text-orange-500" />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Meal Subscriptions</h2>
          <p className="text-gray-500 mb-6">Sign in to subscribe to a recurring meal plan and save.</p>
          <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition">
            <LogIn size={18} /> Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center">
          <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Repeat size={28} className="text-orange-500" />
          </div>
          <h1 className="text-3xl font-extrabold text-gray-900">Meal Plans</h1>
          <p className="text-gray-500 mt-2">Subscribe to a weekly plan — fresh food, delivered on schedule</p>
        </div>

        {/* Feedback message */}
        {msg && (
          <div className={`flex items-center gap-2 p-4 rounded-xl text-sm font-medium ${msg.type === "ok" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
            {msg.type === "ok" ? <CheckCircle size={16} /> : <X size={16} />}
            {msg.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit mx-auto">
          {[["plans", "Available Plans"], ["my", `My Subscriptions${mySubs.length ? ` (${mySubs.length})` : ""}`]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${tab === key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Plans tab */}
        {tab === "plans" && (
          plans.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Package size={40} className="mx-auto mb-3 opacity-30" />
              <p>No plans available right now. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} onSubscribe={handleSubscribe}
                  myActivePlanIds={myActivePlanIds} loading={subLoading} />
              ))}
            </div>
          )
        )}

        {/* My Subscriptions tab */}
        {tab === "my" && (
          mySubs.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Repeat size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">No active subscriptions yet</p>
              <button onClick={() => setTab("plans")}
                className="mt-4 px-5 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600">
                Browse Plans
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {mySubs.map((sub) => {
                const chip = STATUS_CHIP[sub.status] || STATUS_CHIP.active;
                const plan = sub.plan;
                return (
                  <div key={sub.id} className="bg-white rounded-2xl border p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Repeat size={22} className="text-orange-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-gray-900">{plan?.name || `Plan #${sub.plan_id}`}</p>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${chip.cls}`}>{chip.label}</span>
                        </div>
                        {plan && (
                          <p className="text-sm text-gray-500 mt-0.5">
                            ${Number(plan.price).toFixed(2)}{CYCLE_LABEL[plan.billing_cycle]} · {plan.meals_per_cycle} meals
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                          <span className="flex items-center gap-1"><Calendar size={11} /> Started {sub.start_date ? new Date(sub.start_date).toLocaleDateString() : "—"}</span>
                          {sub.next_billing_date && sub.status === "active" && (
                            <span className="flex items-center gap-1"><Clock size={11} /> Next {new Date(sub.next_billing_date).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {sub.status === "active" && (
                        <button onClick={() => handleStatusChange(sub.id, "paused")}
                          className="px-3 py-1.5 border rounded-lg text-xs font-semibold text-yellow-700 hover:bg-yellow-50 transition">
                          Pause
                        </button>
                      )}
                      {sub.status === "paused" && (
                        <button onClick={() => handleStatusChange(sub.id, "active")}
                          className="px-3 py-1.5 border rounded-lg text-xs font-semibold text-green-700 hover:bg-green-50 transition">
                          Resume
                        </button>
                      )}
                      {sub.status !== "cancelled" && (
                        <button onClick={() => { if (window.confirm("Cancel this subscription?")) handleStatusChange(sub.id, "cancelled"); }}
                          className="px-3 py-1.5 border rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 transition">
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SubscriptionsPage;
